import { 
  LayoutDashboard, 
  FileQuestion, 
  BookOpen, 
  Timer, 
  TrendingUp, 
  Trophy, 
  History, 
  Swords,
  Users,
  Brain,
  Settings,
  LogOut,
  Heart,
  Medal,
  Layers,
  ClipboardList,
  Building2,
  Target,
  
  Plus,
  Minus,
  GraduationCap,
  Scale,
  PlayCircle,
  ArrowLeftRight
} from "lucide-react";
import { playClickSound } from "@/lib/sounds";
import { PlanBadge } from "@/components/PlanBadge";
import { Separator } from "@/components/ui/separator";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState, useCallback } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import brandLogoHorizontal from "@/assets/brand/nivela-logo-horizontal.png";
import brandLogoHorizontalDarkAsset from "@/assets/brand/nivela-logo-horizontal-dark.png.asset.json";
const brandLogoHorizontalDark = brandLogoHorizontalDarkAsset.url;
import brandIcon from "@/assets/brand/nivela-icon.png";
import { useAdminPendingCount } from "@/hooks/useAdminPendingCount";
import { useUserRoles } from "@/hooks/useUserRoles";

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

const learnItems: MenuItem[] = [
  { title: "Questões", url: "/questions", icon: FileQuestion },
  { title: "Simulados", url: "/simulados", icon: ClipboardList },
  { title: "Flashcards", url: "/flashcards", icon: Layers },
  { title: "Videoaulas", url: "/videoaulas", icon: PlayCircle },
  { title: "Concursos", url: "/concursos", icon: Building2 },
];

const reviewItems: MenuItem[] = [
  { title: "Favoritos", url: "/favorites", icon: Heart },
  { title: "Meu Caderno", url: "/smart-review", icon: Brain },
  { title: "Pomodoro", url: "/pomodoro", icon: Timer },
];

const progressItems: MenuItem[] = [
  { title: "Desempenho", url: "/analytics", icon: TrendingUp },
  { title: "Conquistas", url: "/achievements", icon: Trophy },
  { title: "Ranking", url: "/ranking", icon: Medal },
  { title: "Histórico", url: "/history", icon: History },
];

const socialItems: MenuItem[] = [
  { title: "Desafios", url: "/challenges", icon: Swords },
  { title: "Grupos", url: "/groups", icon: Users },
];

const planningItems: MenuItem[] = [
  { title: "Plano de Estudos", url: "/concursos/study-plan", icon: Target },
  { title: "Autoavaliação", url: "/concursos/autoavaliacao", icon: Brain },
];

const settingsItems: MenuItem[] = [
  { title: "Configurações", url: "/settings/concursos", icon: Settings },
];



const LEVEL_NAMES = ['Iniciante', 'Estudante', 'Dedicado', 'Avançado', 'Expert', 'Mestre', 'Lenda'];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isAdmin: isAdminRole, isDeveloper } = useUserRoles();
  const visibleLearnItems = (isAdminRole || isDeveloper)
    ? learnItems
    : learnItems.filter((i) => i.url !== '/videoaulas');

  // Collapsible states
  const [learnOpen, setLearnOpen] = useState(true);
  const [planningOpen, setPlanningOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);


  const { profile, userInitials } = useProfile();

  const { isAdmin, pendingCount } = useAdminPendingCount();


  const { data: userStats } = useQuery({
    queryKey: ['sidebar-stats', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const [streakRes, pointsRes] = await Promise.all([
        supabase.from('user_streaks').select('current_streak').eq('user_id', user.id).maybeSingle(),
        supabase.from('user_points').select('current_level').eq('user_id', user.id).eq('module_type', 'concursos').order('current_level', { ascending: false }).limit(1).maybeSingle(),
      ]);
      return {
        streak: streakRes.data?.current_streak || 0,
        level: pointsRes.data?.current_level || 1,
      };
    },
    enabled: !!user,
  });

  // Dynamic badges for pending reviews
  const { data: badges } = useQuery({
    queryKey: ['sidebar-badges', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const today = new Date().toISOString().split('T')[0];
      
      // Flashcards pending for review (next_review_date <= today)
      const { count: pendingFlashcards } = await supabase
        .from('user_flashcard_reviews')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .lte('next_review_date', today);
      
      // Favorites not reviewed in the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data: allFavorites } = await supabase
        .from('favorites')
        .select('question_id')
        .eq('user_id', user.id);
      
      const { data: recentAnswers } = await supabase
        .from('user_answers')
        .select('question_id')
        .eq('is_official', true)
        .eq('user_id', user.id)
        .gte('answered_at', sevenDaysAgo.toISOString());
      
      const answeredIds = new Set(recentAnswers?.map(a => a.question_id) || []);
      const unreviewed = allFavorites?.filter(f => !answeredIds.has(f.question_id)).length || 0;
      
      // Challenge invites por e-mail foram substituídos por código; sem badge.
      const pendingChallengeInvites = 0;

      
      // Pending group invites
      const { count: pendingGroupInvites } = await supabase
        .from('study_group_invites')
        .select('id', { count: 'exact', head: true })
        .eq('invitee_email', user.email)
        .eq('status', 'pending');
      
      // Smart review questions pending
      const { count: pendingSmartReview } = await supabase
        .from('user_question_reviews')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .lte('next_review_date', today);

      return {
        '/flashcards': { count: pendingFlashcards || 0, urgent: false },
        '/favorites': { count: unreviewed, urgent: false },
        '/challenges': { count: pendingChallengeInvites || 0, urgent: true },
        '/groups': { count: pendingGroupInvites || 0, urgent: true },
        '/smart-review': { count: pendingSmartReview || 0, urgent: false },
      } as Record<string, { count: number; urgent: boolean }>;
    },
    enabled: !!user,
    staleTime: 60000,
    refetchInterval: 60000,
  });

  // New content indicators with "mark as seen" support
  const { data: newContent } = useQuery({
    queryKey: ['sidebar-new-content', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      // Check last visit times from localStorage
      const lastVisitQuestions = localStorage.getItem(`last-visit-questions-${user.id}`);
      const lastVisitSimulados = localStorage.getItem(`last-visit-simulados-${user.id}`);
      
      // Get newest question created in last 7 days
      const { data: newestQuestion } = await supabase
        .from('questions')
        .select('created_at')
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      // Get newest simulation created in last 7 days
      const { data: newestSimulation } = await supabase
        .from('simulations')
        .select('created_at')
        .eq('created_via_admin', true)
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      // Show dot only if there's new content AFTER the last visit
      const hasNewQuestions = newestQuestion?.created_at && 
        (!lastVisitQuestions || new Date(newestQuestion.created_at) > new Date(lastVisitQuestions));
      
      const hasNewSimulations = newestSimulation?.created_at && 
        (!lastVisitSimulados || new Date(newestSimulation.created_at) > new Date(lastVisitSimulados));
      
      return {
        '/questions': hasNewQuestions,
        '/simulados': hasNewSimulations,
      } as Record<string, boolean>;
    },
    enabled: !!user,
    staleTime: 300000,
  });

  // Mark page as visited and invalidate query
  const markAsVisited = useCallback((url: string) => {
    if (!user?.id) return;
    
    if (url === '/questions') {
      localStorage.setItem(`last-visit-questions-${user.id}`, new Date().toISOString());
    } else if (url === '/simulados') {
      localStorage.setItem(`last-visit-simulados-${user.id}`, new Date().toISOString());
    }
    
    // Invalidate to refresh the dot status
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['sidebar-new-content'] });
    }, 100);
  }, [user?.id, queryClient]);

  const currentPath = location.pathname;
  const isActive = (path: string) => currentPath === path;


  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const renderMenuItems = (items: MenuItem[]) => (
    <SidebarMenu className={cn(
      "space-y-1",
      collapsed ? "px-0 flex flex-col items-center" : "px-2"
    )}>
      {items.map((item, index) => {
        const active = isActive(item.url);
        const badgeData = badges?.[item.url];
        const hasNew = newContent?.[item.url];
        
        // Rich tooltip content with badge
        const tooltipContent = badgeData && badgeData.count > 0 ? (
          <div className="flex items-center gap-2">
            <span>{item.title}</span>
            <span className={cn(
              "h-5 min-w-5 rounded-full text-xs font-bold flex items-center justify-center px-1.5",
              badgeData.urgent 
                ? "bg-destructive text-destructive-foreground" 
                : "bg-primary text-primary-foreground"
            )}>
              {badgeData.count > 99 ? '99+' : badgeData.count}
            </span>
          </div>
        ) : item.title;

        return (
          <SidebarMenuItem 
            key={item.title}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <SidebarMenuButton 
              asChild 
              tooltip={{
                children: tooltipContent,
                className: badgeData?.count ? "pr-2" : undefined,
              }}
            >
              <NavLink 
                to={item.url} 
                onClick={() => {
                  playClickSound();
                  markAsVisited(item.url);
                }}
                className={cn(
                  "sidebar-menu-item flex items-center rounded-full transition-all duration-200",
                  collapsed 
                    ? "justify-center w-10 h-10 p-0 mx-auto hover:bg-muted/80" 
                    : "gap-3 px-3 py-2.5",
                  active 
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium" 
                    : "hover:bg-muted/60"
                )}
                activeClassName=""
              >
                <div className="relative group/icon">
                  <item.icon className={cn(
                    "sidebar-icon flex-shrink-0 transition-transform duration-200",
                    collapsed ? "h-5 w-5 group-hover/icon:scale-115" : "h-4 w-4",
                    active && "scale-110"
                  )} />
                  {/* New content dot */}
                  {hasNew && !active && !collapsed && (
                    <span className="new-indicator-dot absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
                  )}
                  {/* Mini-badge when collapsed */}
                  {collapsed && badgeData && badgeData.count > 0 && (
                    <span className={cn(
                      "absolute -top-1.5 -right-2 h-4 min-w-4 rounded-full text-[10px] font-bold flex items-center justify-center px-0.5",
                      badgeData.urgent 
                        ? "bg-destructive text-destructive-foreground animate-pulse" 
                        : "bg-primary text-primary-foreground"
                    )}>
                      {badgeData.count > 9 ? '9+' : badgeData.count}
                    </span>
                  )}
                  {/* New dot when collapsed */}
                  {collapsed && hasNew && !active && !(badgeData && badgeData.count > 0) && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full animate-pulse" />
                  )}
                </div>
                {!collapsed && (
                  <>
                    <span className="truncate flex-1">{item.title}</span>
                    {badgeData && badgeData.count > 0 && (
                      <span className={cn(
                        "sidebar-badge h-5 min-w-5 rounded-full text-xs font-medium flex items-center justify-center px-1.5",
                        badgeData.urgent 
                          ? "sidebar-badge-urgent bg-destructive/15 text-destructive" 
                          : "bg-primary/15 text-primary"
                      )}>
                        {badgeData.count > 99 ? '99+' : badgeData.count}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  const CollapsibleHeader = ({ 
    label, 
    isOpen,
    onToggle 
  }: { 
    label: string; 
    isOpen: boolean;
    onToggle: () => void;
  }) => (
    <button 
      onClick={onToggle}
      className="sidebar-group-header flex items-center w-full px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground font-medium hover:text-foreground transition-colors"
    >
      <span>{label}</span>
      {isOpen ? (
        <Minus className="sidebar-expand-icon h-3.5 w-3.5 ml-auto" />
      ) : (
        <Plus className="sidebar-expand-icon h-3.5 w-3.5 ml-auto" />
      )}
    </button>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50">
      <SidebarHeader className="border-b border-border/50 p-4">
        <div className="flex items-center gap-3">
          {collapsed ? (
            <img src={brandIcon} alt="Nível A Educação" className="h-9 w-9 rounded-xl flex-shrink-0" />
          ) : (
            <>
              <img src={brandLogoHorizontal} alt="Nível A Educação" className="h-8 w-auto block dark:hidden" />
              <img src={brandLogoHorizontalDark} alt="Nível A Educação" className="h-8 w-auto hidden dark:block" />
            </>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="flex flex-col px-2 py-4">
        {/* Dashboard */}
        <SidebarGroup className={cn("px-2", collapsed && "flex flex-col items-center")}>
          <SidebarGroupContent>
            <SidebarMenu className={cn(collapsed && "flex flex-col items-center")}>
              <SidebarMenuItem>
                <SidebarMenuButton 
                  asChild 
                  tooltip={{
                    children: userStats ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">Dashboard</span>
                        <span className="text-xs text-muted-foreground">
                          🔥 {userStats.streak} dias • Nível {userStats.level}
                        </span>
                      </div>
                    ) : "Dashboard"
                  }}
                >
                  <NavLink 
                    to="/dashboard" 
                    end
                    className={cn(
                      "flex items-center rounded-full transition-all duration-200",
                      collapsed 
                        ? "justify-center w-10 h-10 p-0 mx-auto hover:bg-muted/80" 
                        : "gap-3 px-3 py-2.5",
                      isActive('/dashboard') 
                        ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium" 
                        : "hover:bg-muted/60"
                    )}
                    activeClassName=""
                  >
                    <div className="group/icon">
                      <LayoutDashboard className={cn(
                        "flex-shrink-0 transition-transform duration-200",
                        collapsed ? "h-5 w-5 group-hover/icon:scale-115" : "h-4 w-4",
                        isActive('/dashboard') && "scale-110"
                      )} />
                    </div>
                    {!collapsed && <span>Dashboard</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {collapsed && <Separator className="my-2 mx-auto w-6 opacity-40" />}

        {/* Aprender */}
        <Collapsible open={learnOpen} onOpenChange={setLearnOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Aprender" 
                  isOpen={learnOpen} 
                  onToggle={() => setLearnOpen(!learnOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(visibleLearnItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(visibleLearnItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {collapsed && <Separator className="my-2 mx-auto w-6 opacity-40" />}

        {/* Planejamento */}
        <Collapsible open={planningOpen} onOpenChange={setPlanningOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader
                  label="Planejamento"
                  isOpen={planningOpen}
                  onToggle={() => setPlanningOpen(!planningOpen)}
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(planningItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(planningItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {collapsed && <Separator className="my-2 mx-auto w-6 opacity-40" />}



        {/* Revisar */}
        <Collapsible open={reviewOpen} onOpenChange={setReviewOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Revisar" 
                  isOpen={reviewOpen} 
                  onToggle={() => setReviewOpen(!reviewOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(reviewItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(reviewItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {collapsed && <Separator className="my-2 mx-auto w-6 opacity-40" />}

        {/* Acompanhar */}
        <Collapsible open={progressOpen} onOpenChange={setProgressOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Acompanhar" 
                  isOpen={progressOpen} 
                  onToggle={() => setProgressOpen(!progressOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(progressItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(progressItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {collapsed && <Separator className="my-2 mx-auto w-6 opacity-40" />}

        {/* Social */}
        <Collapsible open={socialOpen} onOpenChange={setSocialOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Social" 
                  isOpen={socialOpen} 
                  onToggle={() => setSocialOpen(!socialOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(socialItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(socialItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {collapsed && <Separator className="my-2 mx-auto w-6 opacity-40" />}

        {/* Configurações */}
        <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Configurações" 
                  isOpen={settingsOpen} 
                  onToggle={() => setSettingsOpen(!settingsOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(settingsItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(settingsItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

      </SidebarContent>

      <SidebarFooter className="border-t border-border/50 p-4">
        {/* Trocar Módulo */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Trocar Módulo">
              <a
                href="/select-module"
                onClick={(e) => { e.preventDefault(); navigate("/select-module"); }}
                className={cn(
                  "flex items-center rounded-full border border-border bg-muted/50 hover:bg-muted transition-all duration-200 shadow-sm hover:shadow-md mb-2",
                  collapsed ? "justify-center w-10 h-10 p-0 mx-auto" : "gap-3 px-3 py-2.5"
                )}
              >
                <ArrowLeftRight className={cn("flex-shrink-0", collapsed ? "h-5 w-5" : "h-4 w-4")} />
                {!collapsed && <span className="font-medium">Trocar Módulo</span>}
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {/* Plan Badge */}
        {!collapsed && (
          <NavLink to="/plans" className="block px-2 mb-2" activeClassName="" onClick={playClickSound}>
            <div className="flex items-center justify-between rounded-lg bg-muted/60 px-3 py-2 hover:bg-muted transition-colors">
              <span className="text-xs text-muted-foreground">Seu plano</span>
              <PlanBadge />
            </div>
          </NavLink>
        )}
        {/* Admin Link */}
        {isAdmin && (
          <NavLink to="/admin" className="block px-2 mb-2" activeClassName="" onClick={playClickSound}>
            <div className="relative flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2 hover:bg-muted transition-colors">
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
              {!collapsed && <span className="text-xs text-muted-foreground">Painel Admin</span>}
              {pendingCount > 0 && (
                collapsed ? (
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-destructive" />
                ) : (
                  <span className="ml-auto bg-destructive text-destructive-foreground rounded-full px-1.5 py-0.5 text-[10px] font-medium min-w-[18px] text-center">
                    {pendingCount}
                  </span>
                )
              )}
            </div>
          </NavLink>
        )}
        {/* User Profile + Logout */}
        <SidebarMenu>
          <SidebarMenuItem>
            <div className={cn("flex items-center w-full", collapsed ? "flex-col gap-2" : "gap-2")}>
              <SidebarMenuButton tooltip={profile?.first_name || 'Perfil'} className="flex-1 min-w-0 overflow-visible"
                onClick={() => navigate('/profile')}
              >
                <div className="flex items-center gap-3 w-full group">
                  <Avatar className="h-9 w-9 flex-shrink-0 ring-2 ring-border transition-all duration-200 group-hover:ring-foreground/20">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-foreground text-background text-sm font-medium">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  {!collapsed && (
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium truncate">
                        {profile?.first_name || user?.email?.split('@')[0]}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {userStats && `Nível ${userStats.level} • ${userStats.streak}🔥`}
                      </p>
                    </div>
                  )}
                </div>
              </SidebarMenuButton>
              <button 
                onClick={handleSignOut}
                className="p-2 rounded-xl hover:bg-muted/60 transition-all duration-200 text-destructive flex-shrink-0"
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
